import {objectToFormData} from "./FormData";
import http from "./Http";
import {Dispatcher} from "../types";
import {AxiosError} from "axios";
import {isEqual, reduce, set} from "lodash";
import {useState} from "react";

interface FieldData {
    [key: string]: unknown;
}

interface ErrorBag {
    [key: string]: string;
}

interface FormOptions {
    resetOnSuccess: boolean;
}

interface ResponseErrors {
    [key: string]: string[];
}

type RequestType = 'post' | 'put' | 'patch' | 'delete';

export class Form<T> {
    public fields: T & FieldData;
    public errors: ErrorBag;
    public processing: boolean;
    public successful: boolean;
    public setErrors: Dispatcher<ErrorBag>;
    public setProcessing: Dispatcher<boolean>;
    public setSuccessful: Dispatcher<boolean>;
    private initialData: T & FieldData;
    private options: FormOptions;
    private setInitialData: [(T & FieldData), Dispatcher<T & FieldData>][1];
    private setFields: [(T & FieldData), Dispatcher<T & FieldData>][1];

    constructor(initialData: T & FieldData, options: FormOptions = {resetOnSuccess: true}) {

        this.options = options;

        [this.initialData, this.setInitialData] = useState(initialData);
        [this.fields, this.setFields] = useState(initialData);
        [this.errors, this.setErrors] = useState<ErrorBag>({});
        [this.processing, this.setProcessing] = useState<boolean>(false);
        [this.successful, this.setSuccessful] = useState<boolean>(false);
    }

    /**
     * Determine if any of the field data has changed.
     */
    get isDirty(): boolean {
        return !Object.keys(this.initialData).reduce(
            (carry, item) => carry && isEqual(this.initialData[item], this.fields[item]
            ), true);
    }

    /**
     * Re-initialize the form.
     *
     * @param initialData
     */
    withData(initialData: T & FieldData) {
        this.setInitialData(initialData);
        this.setFields(initialData);
        this.setErrors({});

        return this;
    }

    /**
     * Reset the Form to its initial state.
     */
    reset(): void {
        this.withData(this.initialData);
    }

    /**
     * Returns a function for handling onChange events for the fields.
     * @param field
     */
    handleChange(field: string) {
        return (value: unknown) => {
            if (isEqual(this.fields[field], value)) {
                return;
            }
            this.setFields((data: T & FieldData) => set({...data}, field, value));
            this.clearError(field);
        };
    }

    setField(field: string, value: unknown) {
        this.setFields((data: T & FieldData) => set({...data}, field, value));
        this.clearError(field);
    }

    data(): FieldData {
        return Object.keys(this.initialData).reduce((carry: FieldData, item: string) => {
            carry[item] = this.fields[item];
            return carry;
        }, {});
    }

    /**
     * Determine if the form has any errors. If keys are provided, the check will be limited to those keys.
     *
     * @param keys
     */
    hasErrors(keys: string[] = []): boolean {

        const errorKeys = Object.keys(this.errors);

        if (keys.length === 0) {
            return errorKeys.length > 0;
        }

        return keys.some((key) => this.hasError(key));
    }

    /**
     * Determine if the form has an error for the specified field.
     */
    hasError(field: string): boolean {
        let hasError = field in this.errors;

        if (!hasError) {
            const errors = Object.keys(this.errors).filter(
                (e) => e.startsWith(`${field}.`) || e.startsWith(`${field}[`)
            );

            hasError = errors.length > 0;
        }

        return hasError;
    }

    getError(field: string) {
        return field in this.errors ? this.errors[field] : '';
    }

    /**
     * Clear the error for a specific field.
     *
     * @param field
     */
    clearError(field: string | null = null): void {
        if (!field) {
            this.setErrors({});

            return;
        }

        this.setErrors((errors: ErrorBag) => Object.keys(errors)
            .reduce((carry, name) => {
                if (this.nameMatchesField(name, field)) {
                    delete carry[name];
                }

                return carry;
            }, errors));
    }

    /**
     * Submit the form data via a POST request.
     *
     * @param url
     */
    post(url: string) {
        return this.submit('post', url);
    }

    /**
     * Submit the form data via a PUT request.
     *
     * @param url
     */
    put(url: string) {
        return this.submit('put', url);
    }

    /**
     * Submit the form data via a PATCH request.
     *
     * @param url
     */
    patch(url: string) {
        return this.submit('patch', url);
    }

    /**
     * Submit the form data via a DELETE request.
     *
     * @param url
     */
    delete(url: string) {
        return this.submit('delete', url);
    }

    /**
     * Compare the error name with the field name.
     *
     * @param name
     * @param field
     * @protected
     */
    protected nameMatchesField(name: string, field: string): boolean {
        return name === field || name.startsWith(`${field}.`) || name.startsWith(`${field}[`);
    }

    /**
     * Submit the form data.
     *
     * @param requestType
     * @param url
     */
    protected submit(requestType: RequestType, url: string) {
        this.clearError();
        this.setSuccessful(false);
        this.setProcessing(true);

        let data = {};

        if (requestType !== 'delete') {
            const formData = this.data();
            data = this.hasFiles(formData) ? objectToFormData(formData) : formData;
        }

        return new Promise((resolve, reject) => {
            http[requestType](url, data)
                .then((response) => {
                    this.onSuccess();
                    resolve(response.data);
                })
                .catch((error) => {
                    this.onFail(error);
                    reject(error);
                })
                .then(() => this.setProcessing(false));
        });
    }

    /**
     * Handle a successful form submission.
     */
    protected onSuccess(): void {
        this.setSuccessful(true);

        if (this.options.resetOnSuccess) {
            this.reset();
        }
    }

    /**
     * Handle a failed form submission.
     *
     * @param {object} error
     */
    protected onFail(error: AxiosError<{ errors: ResponseErrors }>): void {
        this.setSuccessful(false);

        if (error.response?.data?.errors) {
            this.recordErrors(error.response.data.errors);
        }
    }

    protected recordErrors(errors: ResponseErrors): void {
        this.setErrors(
            Object.keys(errors).reduce((carry: ErrorBag, field) => {
                carry[field] = errors[field][0];
                return carry;
            }, {})
        );
    }

    protected hasFiles(object: object) {
        return reduce(object, (carry, item) => carry || this.hasFilesDeep(item), false);
    }

    handleRadio(field: string, value: unknown) {
        this.setFields((data: T & FieldData) => set({...data}, field, value));
        this.clearError(field);
    }


    protected hasFilesDeep(object: Iterable<unknown> | unknown): boolean {
        if (object === null) {
            return false;
        }

        if (typeof object === 'object') {
            return reduce<object, boolean>(object, (carry, item) => carry || this.hasFilesDeep(item), false);
        }

        if (Array.isArray(object)) {
            for (const key in object) {
                if (key in object) {
                    return this.hasFilesDeep(object[key]);
                }
            }
        }

        return object instanceof File || object instanceof FileList;
    }
}

export function useForm<T>(initialData: T & FieldData, options: FormOptions = {resetOnSuccess: true}) {
    return new Form(initialData, options);
}
